import React, { useState, useEffect, useRef } from 'react';
import { useFirebase } from '../contexts/FirebaseContext';
import { useWordContext } from '../context/WordContext';
import apiClient from '../utils/apiClient';
import '../styles/chatbot.css';
import processChatbotSentence from '../utils/processChatbotSentence';
import ChatbotDictionaryPanel from '../components/ChatbotDictionaryPanel';
import chatMemoryManager from '../utils/chatMemoryManager';
import { getSystemPrompt } from '../utils/chatbotService';
import { ReactComponent as BabbleFishOn } from '../images/icons/babble-fish-on.svg';
import { ReactComponent as BabbleFishOff } from '../images/icons/babble-fish-off.svg';
import tipsOnIcon from '../images/icons/tips-on.svg';
import tipsOffIcon from '../images/icons/tips-off.svg';
import userActivityService from '../services/userActivityService';
import logger from '../utils/logger';
import { 
  getConversations, 
  saveConversation, 
  updateConversation, 
  resetConversations 
} from '../utils/mongodb';
import { ReactComponent as EditIcon } from '../images/icons/edit2.svg';
import { ReactComponent as DeleteIcon } from '../images/icons/delete-icon.svg';
import { Link } from 'react-router-dom';
import Pricing from './Pricing';

const Chatbot = () => {
  const { user, isAuthenticated, userPlan, planExpiresAt } = useFirebase();
  const { } = useWordContext(); // Keep the context but don't destructure savedWords
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [chatMode, setChatMode] = useState('bojee'); // 'bojee' (Bojee Mode) or 'custom' (Custom Mode)
  const [politenessMode, setPolitenessMode] = useState('polite'); // 'polite' or 'casual'
  const [username, setUsername] = useState('');
  const [settings, setSettings] = useState({
    learningLanguage: '',  // Start with empty string to avoid default
    languageLevel: 'Beginner',
    spanishRegion: 'Latin America',
    memoryEnabled: false   // Default to false (OFF)
  });
  const [isNewConversation, setIsNewConversation] = useState(true); // Track if this is a new conversation
  const [settingsLoaded, setSettingsLoaded] = useState(false); // Track if settings are loaded
  const [isDbLoading, setIsDbLoading] = useState(true); // Track if database conversation load is in progress
  const [translatedMessages, setTranslatedMessages] = useState({}); // { messageIndex: boolean }
  const [selectedWord, setSelectedWord] = useState(null); // Store the selected word for dictionary
  const [selectedSentence, setSelectedSentence] = useState(''); // Store the sentence context
  const [dictionaryOpen, setDictionaryOpen] = useState(false); // Dictionary closed by default
  const [isOverCharLimit, setIsOverCharLimit] = useState(false); // Track if over character limit
  const [charOverLimit, setCharOverLimit] = useState(0); // Track how many characters over the limit
  const [memoryFacts, setMemoryFacts] = useState([]); // All memory facts
  const [showMemoryDebug, setShowMemoryDebug] = useState(false); // Toggle to show memory debug panel
  const [fluentMode, setFluentMode] = useState(false); // Toggle for Fluent Mode
  const [isTransformingText, setIsTransformingText] = useState(false); // Loading state for text transformation
  const [evaluation, setEvaluation] = useState(null); // Store the evaluation result
  const [isEvaluating, setIsEvaluating] = useState(false); // Loading state for evaluation
  // New state variables for custom mode
  const [customPrompt, setCustomPrompt] = useState(''); // Custom prompt text
  const [customName, setCustomName] = useState(''); // Custom name for the chatbot
  // Store separate messages for each mode
  const [bojeeMessages, setBojeeMessages] = useState([]);
  const [customMessages, setCustomMessages] = useState([]);
  // Track evaluation usage limit
  const [evaluationCount, setEvaluationCount] = useState(0);
  const [maxEvaluations, setMaxEvaluations] = useState(2);
  const [isEvaluationLimitChecked, setIsEvaluationLimitChecked] = useState(false);
  // New state variables for tips
  const [showTips, setShowTips] = useState(false);
  const [tipsRead, setTipsRead] = useState(localStorage.getItem("chatbotTipsRead") === "true");
  const [isClosing, setIsClosing] = useState(false);
  // Track fluent mode usage separately for Bojee and Custom modes
  const [hasUsedFluentModeInBojee, setHasUsedFluentModeInBojee] = useState(false);
  const [hasUsedFluentModeInCustom, setHasUsedFluentModeInCustom] = useState(false);
  const [currentConversationId, setCurrentConversationId] = useState(null);
  // State to track which message is being edited
  const [editingMessageIndex, setEditingMessageIndex] = useState(null);
  const [editedMessageContent, setEditedMessageContent] = useState('');
  const [showDebugInfo, setShowDebugInfo] = useState('disabled'); // Add debug info state
  // Message limit tracking
  const [messageLimit, setMessageLimit] = useState({
    bojee: { count: 0, limit: 25, hasReached: false },
    custom: { count: 0, limit: 25, hasReached: false }
  });
  const [isCheckingMessageLimit, setIsCheckingMessageLimit] = useState(false);
  
  const messagesEndRef = useRef(null);
  const chatContainerRef = useRef(null);
  const inputRef = useRef(null); // Reference for the textarea input for focus
  const saveToMongoDBDebounceTimeoutRef = useRef(null); // Reference for debounce timeout
  const CHARACTER_LIMIT = 2500; // Character limit constant
  
  // Add state for showing pricing overlay
  const [showPricingOverlay, setShowPricingOverlay] = useState(false);
  
  // Add new state for overlays
  const [activeOverlay, setActiveOverlay] = useState(null); // null, 'chatMode', 'politenessMode', 'dictionary'
  
  // Add missing state variables
  const [resetOnNextLoad, setResetOnNextLoad] = useState(false);
  const [isCreatingNewConversation, setIsCreatingNewConversation] = useState(false);
  
  const handleShowPricing = (e) => {
    e.preventDefault();
    setShowPricingOverlay(true);
  };
  
  const handleClosePricing = () => {
    setShowPricingOverlay(false);
  };
  
  // Set document title based on the chatMode and customName
  useEffect(() => {
    if (chatMode === 'custom' && customName) {
      document.title = `Chat with ${customName}`;
    } else {
      document.title = 'Chat with Bojee';
    }
  }, [chatMode, customName]);

  // Ensure Bojee mode is selected by default
  useEffect(() => {
    // If chatMode is empty or undefined, set it to 'bojee'
    if (!chatMode) {
      setChatMode('bojee');
      logger.debug('Chatbot - Setting default mode to Bojee');
    }
  }, [chatMode]);

  // Scroll to bottom when messages change
  useEffect(() => {
    if (messagesEndRef.current) {
      messagesEndRef.current.scrollIntoView({ behavior: 'smooth' });
    }
  }, [messages]);

  // Function to clear localStorage items related to conversations for the current language
  const clearCurrentLanguageConversations = () => {
    if (!isAuthenticated || !user || !settings.learningLanguage) return;
    
    logger.debug(`Clearing conversations for ${settings.learningLanguage} after 4 AM reset`);
    
    // Clear bojee messages
    localStorage.removeItem(`chatbot_bojee_messages_${user.uid}_${settings.learningLanguage}`);
    
    // Clear custom messages
    localStorage.removeItem(`chatbot_custom_messages_${user.uid}_${settings.learningLanguage}`);
    
    // Reset messages in the UI
    setMessages([]);
    setBojeeMessages([]);
    setCustomMessages([]);
    setIsNewConversation(true);
    
    logger.debug(`Conversations cleared for ${settings.learningLanguage} after 4 AM reset`);
  };

  // Effect 1: Fetch user settings (like learning language) and profile.
  // This runs first to get essential non-conversation data.
  useEffect(() => {
    const fetchInitialSettingsAndProfile = async () => {
      logger.info('EFFECT 1 (Initial Settings & Profile) RUNNING', { isAuthenticated, hasUser: !!user });
      if (!isAuthenticated || !user) {
        setSettingsLoaded(false);
        setSettings(prev => ({ ...prev, learningLanguage: '' }));
        setMessages([]);
        setBojeeMessages([]);
        setCustomMessages([]);
        setIsNewConversation(true);
        setCurrentConversationId(null);
        // Reset other relevant states if user logs out
        setCustomName('');
        setCustomPrompt('');
        setPolitenessMode('polite');
        setHasUsedFluentModeInBojee(false);
        setHasUsedFluentModeInCustom(false);
        setUsername('');
        setIsDbLoading(true); // Reset DbLoading if user logs out, new load will be needed
        logger.info('EFFECT 1: User logged out or not authenticated. State cleared.');
        return;
      }

      // Reset isDbLoading to true at the start of fetching user data,
      // as a new DB load (Effect 2) will follow.
      setIsDbLoading(true);

      try {
        const profileResponse = await apiClient.get('/api/profile');
        if (profileResponse.ok) {
          const profileData = await profileResponse.json();
          setUsername(profileData.username || user.displayName || 'User');
        } else {
          setUsername(user.displayName || 'User');
        }

        const settingsResponse = await apiClient.get('/api/settings');
        let initialLearningLanguage = 'Spanish';

        if (settingsResponse.ok) {
          const settingsData = await settingsResponse.json();
          initialLearningLanguage = settingsData.learningLanguage || 'Spanish';
          setSettings({
            ...settingsData,
            learningLanguage: initialLearningLanguage,
            memoryEnabled: localStorage.getItem('memoryEnabled') === 'true'
          });
        } else {
          logger.error('Chatbot - EFFECT 1: Failed to fetch settings:', await settingsResponse.text());
          setSettings(prev => ({
            ...prev,
            learningLanguage: initialLearningLanguage,
            memoryEnabled: localStorage.getItem('memoryEnabled') === 'true'
          }));
        }
        setSettingsLoaded(true);
        logger.info(`EFFECT 1: Initial settings and profile loaded. Language: ${initialLearningLanguage}. SettingsLoaded: true.`);
      } catch (error) {
        logger.error('Chatbot - EFFECT 1: Error fetching initial settings/profile:', error);
        setSettings(prev => ({ ...prev, learningLanguage: 'Spanish', memoryEnabled: localStorage.getItem('memoryEnabled') === 'true' }));
        setUsername(user?.displayName || 'User');
        setSettingsLoaded(true);
      }
      // Note: setIsDbLoading(false) is NOT set here; Effect 2 handles that after its own async work.
    };
    fetchInitialSettingsAndProfile();
  }, [isAuthenticated, user]);

  // Effect 2: Load conversations from DB (primary source for new day).
  // This runs after settings (especially learningLanguage) are loaded, or if chatMode changes.
  useEffect(() => {
    const loadPrimaryConversations = async () => {
      if (!isAuthenticated || !user || !settings.learningLanguage || !settingsLoaded) {
        logger.info('EFFECT 2 (DB Load): Skipping (prerequisites not met). Setting isDbLoading to false.', { 
          isAuthenticated, 
          hasUser: !!user, 
          lang: settings.learningLanguage, 
          settingsLoaded 
        });
        setIsDbLoading(false); // Ensure isDbLoading is false if we skip
        // If settings ARE loaded but there's NO language, it implies user might have cleared it.
        // In this case, messages should be empty.
        if (settingsLoaded && !settings.learningLanguage) {
           setMessages([]);
           setBojeeMessages([]);
           setCustomMessages([]);
           setIsNewConversation(true);
           setCurrentConversationId(null);
        }
        return;
      }

      logger.info(`EFFECT 2 (DB Load): STARTING. Lang: ${settings.learningLanguage}, Mode: ${chatMode}. Setting isDbLoading to true.`);
      setIsDbLoading(true); // Set loading true at the beginning of the async operation

      try {
        const conversationsFromDB = await getConversations(user.uid, settings.learningLanguage);
        logger.info('EFFECT 2 (DB Load): Fetched from DB:', conversationsFromDB);
        const bojeeConvDB = conversationsFromDB.find(conv => conv.chatMode === 'bojee');
        const customConvDB = conversationsFromDB.find(conv => conv.chatMode === 'custom');

        logger.info(`EFFECT 2 (DB Load): Clearing localStorage message caches for ${settings.learningLanguage}.`);
        localStorage.removeItem(`chatbot_bojee_messages_${user.uid}_${settings.learningLanguage}`);
        localStorage.removeItem(`chatbot_custom_messages_${user.uid}_${settings.learningLanguage}`);

        let loadedBojeeMessages = [];
        let loadedHasUsedFluentModeInBojee = false;
        let bojeePoliteness = 'polite';
        if (bojeeConvDB) {
          loadedBojeeMessages = bojeeConvDB.messages || [];
          loadedHasUsedFluentModeInBojee = bojeeConvDB.usedFluentMode === true;
          bojeePoliteness = bojeeConvDB.politenessMode || 'polite';
          logger.info('EFFECT 2 (DB Load): Bojee DB Data Found.', { 
            id: bojeeConvDB._id, 
            msgCount: loadedBojeeMessages.length, 
            fluent: loadedHasUsedFluentModeInBojee 
          });
        } else {
          logger.info('EFFECT 2 (DB Load): No Bojee DB Data Found for this language.');
        }
        setBojeeMessages(loadedBojeeMessages);
        setHasUsedFluentModeInBojee(loadedHasUsedFluentModeInBojee);

        let loadedCustomMessages = [];
        let loadedHasUsedFluentModeInCustom = false;
        let loadedCustomName = '';
        let loadedCustomPrompt = '';
        let customPoliteness = 'polite';
        if (customConvDB) {
          loadedCustomMessages = customConvDB.messages || [];
          loadedHasUsedFluentModeInCustom = customConvDB.usedFluentMode === true;
          loadedCustomName = customConvDB.customName || '';
          loadedCustomPrompt = customConvDB.customPrompt || '';
          customPoliteness = customConvDB.politenessMode || 'polite';
          logger.info('EFFECT 2 (DB Load): Custom DB Data Found.', { 
            id: customConvDB._id, 
            msgCount: loadedCustomMessages.length, 
            name: loadedCustomName, 
            fluent: loadedHasUsedFluentModeInCustom 
          });
        } else {
          logger.info('EFFECT 2 (DB Load): No Custom DB Data Found for this language.');
        }
        setCustomMessages(loadedCustomMessages);
        setHasUsedFluentModeInCustom(loadedHasUsedFluentModeInCustom);
        setCustomName(loadedCustomName);
        setCustomPrompt(loadedCustomPrompt);

        if (chatMode === 'bojee') {
          logger.info('EFFECT 2 (DB Load): BEFORE setMessages (Bojee)', { loadedBojeeMessages });
          setMessages(loadedBojeeMessages);
          setCurrentConversationId(bojeeConvDB ? bojeeConvDB._id : null);
          setIsNewConversation(loadedBojeeMessages.length === 0);
          setPolitenessMode(bojeePoliteness);
        } else { // chatMode === 'custom'
          logger.info('EFFECT 2 (DB Load): BEFORE setMessages (Custom)', { loadedCustomMessages });
          setMessages(loadedCustomMessages);
          setCurrentConversationId(customConvDB ? customConvDB._id : null);
          setIsNewConversation(loadedCustomMessages.length === 0);
          setPolitenessMode(customPoliteness);
        }
        logger.info(`EFFECT 2 (DB Load): Finished processing. Active messages length: ${messages.length}`); // Log after state is set

      } catch (error) {
        logger.error('EFFECT 2 (DB Load): Error loading conversations from DB:', error);
        setMessages([]);
        setBojeeMessages([]);
        setCustomMessages([]);
        setIsNewConversation(true);
        setCurrentConversationId(null);
        if (user && settings.learningLanguage) {
            localStorage.removeItem(`chatbot_bojee_messages_${user.uid}_${settings.learningLanguage}`);
            localStorage.removeItem(`chatbot_custom_messages_${user.uid}_${settings.learningLanguage}`);
        }
      } finally {
        logger.info('EFFECT 2 (DB Load): FINALLY block. Setting isDbLoading to false.');
        setIsDbLoading(false); // Set loading false when operation completes (success or fail)
      }
    };

    loadPrimaryConversations();
  }, [isAuthenticated, user, settings.learningLanguage, settingsLoaded, chatMode]);

  // Effect 3: Save current messages and settings to localStorage (for quick in-session restoration).
  // This runs *after* messages state has been set by DB load or user interaction.
  useEffect(() => {
    if (!isAuthenticated || !user || !settings.learningLanguage || !settingsLoaded) return;

    // Only save to localStorage if there are actual messages to save FROM THE CURRENT SESSION.
    // An empty messages array (start of new day) should not create an empty localStorage item here
    // if the goal is to allow quick refresh *during a session with messages*.
    // However, since Effect 2 clears localStorage for a new day, this save is fine for the current session.
    if (messages.length > 0) {
      logger.debugComponent('Chatbot', `Effect 3: Saving ${messages.length} messages to localStorage for mode: ${chatMode}`);
      if (chatMode === 'bojee') {
        localStorage.setItem(`chatbot_bojee_messages_${user.uid}_${settings.learningLanguage}`, JSON.stringify(messages));
      } else { // custom
        localStorage.setItem(`chatbot_custom_messages_${user.uid}_${settings.learningLanguage}`, JSON.stringify(messages));
      }
    } else {
      // If messages are empty (e.g., new day, or user cleared), ensure corresponding localStorage is also empty.
      // This complements the clearing in Effect 2.
      logger.debugComponent('Chatbot', `Effect 3: Messages are empty for mode ${chatMode}, ensuring localStorage is clear.`);
      if (chatMode === 'bojee') {
       localStorage.removeItem(`chatbot_bojee_messages_${user.uid}_${settings.learningLanguage}`);
      } else {
       localStorage.removeItem(`chatbot_custom_messages_${user.uid}_${settings.learningLanguage}`);
      }
    }

    // Always save current mode, politeness, etc., to localStorage for session persistence.
    localStorage.setItem(`chatbot_mode_${user.uid}_${settings.learningLanguage}`, chatMode);
    localStorage.setItem(`chatbot_politeness_mode_${user.uid}_${settings.learningLanguage}`, politenessMode);
    if (chatMode === 'custom') {
      localStorage.setItem(`chatbot_custom_name_${user.uid}_${settings.learningLanguage}`, customName);
      localStorage.setItem(`chatbot_custom_prompt_${user.uid}_${settings.learningLanguage}`, customPrompt);
    }
    // Save fluent mode usage status to localStorage for quick UI reflection if needed,
    // though DB is the truth.
    localStorage.setItem(`chatbot_fluent_bojee_used_${user.uid}_${settings.learningLanguage}`, hasUsedFluentModeInBojee.toString());
    localStorage.setItem(`chatbot_fluent_custom_used_${user.uid}_${settings.learningLanguage}`, hasUsedFluentModeInCustom.toString());

  }, [messages, chatMode, politenessMode, customName, customPrompt, isAuthenticated, user, settings.learningLanguage, settingsLoaded, hasUsedFluentModeInBojee, hasUsedFluentModeInCustom]);

  // Effect 4: Save current messages and relevant state to MongoDB.
  // This runs after messages state has been updated by DB load or user interaction.
  useEffect(() => {
    const effect4Timestamp = Date.now();
    logger.info(`EFFECT 4 (MongoDB Save) TRIGGERED at ${effect4Timestamp}.`, {
      isDbLoading,
      isAuthenticated,
      hasUser: !!user,
      lang: settings.learningLanguage,
      settingsLoaded,
      messagesDefined: messages !== undefined,
      messagesLength: messages?.length,
    });

    if (isDbLoading) {
      logger.info(`EFFECT 4 at ${effect4Timestamp}: SKIPPING MongoDB save because isDbLoading is TRUE.`);
      clearTimeout(saveToMongoDBDebounceTimeoutRef.current);
      return;
    }
    if (!isAuthenticated || !user || !settings.learningLanguage || !settingsLoaded || messages === undefined) {
      logger.info(`EFFECT 4 at ${effect4Timestamp}: SKIPPING MongoDB save due to other prerequisites.`);
      clearTimeout(saveToMongoDBDebounceTimeoutRef.current);
      return;
    }

    logger.info(`EFFECT 4 at ${effect4Timestamp}: Scheduling saveToMongoDB. messages.length: ${messages.length}`);

    clearTimeout(saveToMongoDBDebounceTimeoutRef.current);
    saveToMongoDBDebounceTimeoutRef.current = setTimeout(() => {
        logger.info(`DEBOUNCED saveToMongoDB executing NOW at ${Date.now()} from Effect 4 run @${effect4Timestamp}. messages.length: ${messages?.length} (checking messages directly from state)`);
        saveToMongoDB(messages); // Pass current messages state
    }, 500); // Debounce for 500ms (adjust if needed)

    return () => {
        clearTimeout(saveToMongoDBDebounceTimeoutRef.current);
    };

  }, [
      messages, chatMode, politenessMode, customName, customPrompt,
      currentConversationId, hasUsedFluentModeInBojee, hasUsedFluentModeInCustom,
      isAuthenticated, user, settings.learningLanguage, settingsLoaded,
      isDbLoading // Add isDbLoading to dependency array
  ]);

  // Function to clear localStorage items related to a previous language
  const clearPreviousLanguageLocalStorage = (previousLanguage) => {
    if (!previousLanguage || !user) return;
    logger.debugComponent('Chatbot', `Clearing localStorage for previous language: ${previousLanguage}`);
    localStorage.removeItem(`chatbot_bojee_messages_${user.uid}_${previousLanguage}`);
    localStorage.removeItem(`chatbot_custom_messages_${user.uid}_${previousLanguage}`);
    localStorage.removeItem(`chatbot_chat_mode_${user.uid}_${previousLanguage}`);
    localStorage.removeItem(`chatbot_politeness_mode_${user.uid}_${previousLanguage}`);
    localStorage.removeItem(`chatbot_custom_name_${user.uid}_${previousLanguage}`);
    localStorage.removeItem(`chatbot_custom_prompt_${user.uid}_${previousLanguage}`);
    localStorage.removeItem(`chatbot_hasUsedFluentModeInBojee_${user.uid}_${previousLanguage}`);
    localStorage.removeItem(`chatbot_hasUsedFluentModeInCustom_${user.uid}_${previousLanguage}`);
  };

  // Check for 4AM reset when component mounts
  // REMOVED: We now handle this in the loadConversations function when fetching from MongoDB

  // Refresh data when settings change (e.g., language is switched)
  useEffect(() => {
    // Instead of polling, let's use a storage event listener
    // This way we only update when settings actually change
    const handleStorageChange = (e) => {
      if (e.key === 'learningLanguage' && e.newValue !== settings.learningLanguage) {
        logger.debug(`Chatbot - Detected language change to ${e.newValue}`);
        setSettings(prev => ({
          ...prev,
          learningLanguage: e.newValue
        }));
      }
    };
    
    // Listen for localStorage changes (when settings are updated elsewhere)
    window.addEventListener('storage', handleStorageChange);
    
    // Clean up the event listener
    return () => window.removeEventListener('storage', handleStorageChange);
  }, [settings.learningLanguage]);
  
  // Remove the initialization effect that was adding welcome messages
  useEffect(() => {
    if (settingsLoaded && settings.learningLanguage && messages.length === 0) {
      // Check if we have cached messages before showing empty messages
      const cachedBojeeMessages = localStorage.getItem(`chatbot_bojee_messages_${user.uid}_${settings.learningLanguage}`);
      const cachedCustomMessages = localStorage.getItem(`chatbot_custom_messages_${user.uid}_${settings.learningLanguage}`);
      
      if (chatMode === 'custom') {
        if (!cachedCustomMessages) {
          setMessages([]);
          setCustomMessages([]);
          logger.debug(`Chatbot - Added empty custom conversation for ${settings.learningLanguage} (no cache found)`);
        } else {
          logger.debug(`Chatbot - Not showing empty messages for custom mode as cache exists`);
        }
      } else { // Bojee mode
        if (!cachedBojeeMessages) {
          setMessages([]);
          setBojeeMessages([]);
          logger.debug(`Chatbot - Added empty bojee conversation for ${settings.learningLanguage} (no cache found)`);
        } else {
          logger.debug(`Chatbot - Not showing empty messages for bojee mode as cache exists`);
        }
      }
    }
  }, [settingsLoaded, settings.learningLanguage, messages.length, isAuthenticated, user, chatMode]);

  // Load memory facts when user is authenticated
  useEffect(() => {
    if (isAuthenticated && user) {
      // Load all memory facts - using async getAllFacts
      const loadMemoryFacts = async () => {
        const facts = await chatMemoryManager.getAllFacts(user.uid);
        setMemoryFacts(facts);
        logger.debug(`Chatbot - Loaded ${facts.length} memory facts`);
      };
      
      loadMemoryFacts();
    }
  }, [isAuthenticated, user]);

  // Load all memory details when debug panel is shown
  useEffect(() => {
    if (showMemoryDebug && isAuthenticated && user) {
      // Load full memory including timestamps - using async getMemory
      const loadMemoryDetails = async () => {
        const memory = await chatMemoryManager.getMemory(user.uid);
        setMemoryFacts(memory.map(item => item.fact));
      };
      
      loadMemoryDetails();
    }
  }, [showMemoryDebug, isAuthenticated, user]);

  // Transform user input to fluent language
  const transformToFluentLanguage = async (text) => {
    if (!text.trim() || !fluentMode) return text;
    
    try {
      setIsTransformingText(true);
      const currentLanguage = settings.learningLanguage || 'Spanish';
      
      logger.debug('==== FLUENT MODE TRANSFORMATION ====');
      logger.debug('Original text:', text);
      logger.debug('Politeness mode:', politenessMode);
      
      // Get Japanese speech style if the language is Japanese
      const japaneseSpeechStyle = currentLanguage === 'Japanese' 
        ? localStorage.getItem('japaneseSpeechStyle') || 'neutral'
        : 'neutral';
      
      if (currentLanguage === 'Japanese') {
        logger.debug('Japanese speech style:', japaneseSpeechStyle);
      }
      
      // Get Chinese character type if the language is Chinese
      const chineseCharacterType = currentLanguage === 'Chinese'
        ? localStorage.getItem('chineseCharacterType') || 'Simplified'
        : 'Simplified';
      
      if (currentLanguage === 'Chinese') {
        logger.debug('Chinese character type:', chineseCharacterType);
      }
      
      // Improved prompt with specific instructions and special handling for Japanese
      let transformationPrompt;
      
      if (currentLanguage === 'Japanese') {
        const politenessInstruction = politenessMode === 'polite' 
          ? "Use polite Japanese with '-masu' and '-desu' endings. Maintain formal 丁寧語 (teineigo)."
          : "Use casual Japanese as if speaking with a friend. Avoid '-masu' and '-desu' endings unless necessary. Use casual expressions like 'さあ', 'まあ', 'そうだな', etc.";
        
        // Add speech style instructions
        let speechStyleInstruction = "";
        if (japaneseSpeechStyle === 'feminine') {
          speechStyleInstruction = `Use a feminine speech style with:
- The pronoun "私 (watashi)" for "I"
- Softer sentence endings like "わ (wa)", "かしら (kashira)", or "の (no)" when appropriate
- More polite expressions and hedges
- Avoid masculine expressions like "俺 (ore)", "僕 (boku)", or sentence endings like "ぜ (ze)" or "ぞ (zo)"
- In ${politenessMode === 'casual' ? 'casual' : 'polite'} contexts, use expressions that are natural for women`;
        } else if (japaneseSpeechStyle === 'masculine') {
          speechStyleInstruction = `Use a masculine speech style with:
- The pronoun ${politenessMode === 'casual' ? '"俺 (ore)" or "僕 (boku)"' : '"僕 (boku)" or "私 (watashi)"'} for "I"
- More direct expressions and sentence structures
- Sentence endings like ${politenessMode === 'casual' ? '"だ (da)", "ぜ (ze)", or "ぞ (zo)"' : 'more masculine-typical formal endings'} when appropriate
- Avoid feminine expressions like "わ (wa)" or "かしら (kashira)" as sentence endings
- In ${politenessMode === 'casual' ? 'casual' : 'polite'} contexts, use expressions that are natural for men`;
        } else { // neutral
          speechStyleInstruction = `Use gender-neutral speech style with:
- The pronoun "私 (watashi)" for "I"
- Standard ${politenessMode === 'casual' ? 'casual' : 'polite'} expressions without strong gender markers
- Avoid strongly masculine expressions like "俺 (ore)" or sentence endings like "ぜ (ze)" or "ぞ (zo)"
- Avoid strongly feminine sentence endings like "わ (wa)" or "かしら (kashira)"
- Use expressions that would be natural for any gender`;
        }
        
        transformationPrompt = `Transform ONLY the following text in Japanese to a grammatically correct and natural Japanese version:
      
"${text}"

The text was written by a Japanese language learner, so correct any grammar, particle usage, word choice, or sentence structure mistakes while keeping the original meaning.
Important instructions:
1. ONLY correct THIS EXACT TEXT above - do not invent new content or provide examples
2. Do not add any explanations, labels, or commentary in English or Japanese
3. Do not output phrases like "修正版:" or "訂正:" or any other labels
4. Return ONLY the corrected Japanese text, with no other text before or after it
5. ${politenessInstruction}
6. ${speechStyleInstruction}
7. Fix any incorrect kanji, hiragana, or katakana usage

For example, if input is "私は昨日映画を見ました物です" your entire output should be just "私は昨日映画を見ました"`;
      } else if (currentLanguage === 'Chinese') {
        // Special handling for Chinese with character type
        const characterTypeInstruction = `IMPORTANT: Use ONLY ${chineseCharacterType} Chinese characters in your response. Do not mix Simplified and Traditional characters.`;
        
        transformationPrompt = `Transform ONLY the following text in Chinese to a grammatically correct and natural Chinese version:
      
"${text}"

The text was written by a Chinese language learner, so correct any grammar, word choice, or sentence structure mistakes while keeping the original meaning.
Important instructions:
1. ONLY correct THIS EXACT TEXT above - do not invent new content or provide examples
2. Do not add any explanations, labels, or commentary in English or Chinese
3. Do not output phrases like "更正:" or "修正:" or any other labels
4. Return ONLY the corrected Chinese text, with no other text before or after it
5. ${characterTypeInstruction}
6. Fix any incorrect character usage or word order

For example, if input is "我去图书馆学习中文" (in Simplified) or "我去圖書館學習中文" (in Traditional), keep the same character type in your response.`;
      } else {
        // For Romance languages (Spanish, French, Italian)
        const politenessInstruction = politenessMode === 'polite'
          ? `Use formal language with formal pronouns like "usted" (Spanish), "vous" (French), or "Lei" (Italian). This is known as "vouvoyer" in French.`
          : `Use informal language with informal pronouns like "tú" (Spanish), "tu" (French), or "tu" (Italian). This is known as "tutoyer" in French.`;
        
        transformationPrompt = `Transform ONLY the following text in ${currentLanguage} to a grammatically correct and fluent version in ${currentLanguage}:
      
"${text}"

The text was written by a language learner, so correct any grammar or vocabulary mistakes while keeping the original meaning.
Important instructions:
1. ONLY correct THIS EXACT TEXT above - do not invent new content or provide examples
2. Do not add any explanations, labels, or commentary
3. Do not output phrases like "Corrected version:" or "Here's the fluent version:"
4. Return ONLY the corrected text, with no other text before or after it
5. ${politenessInstruction}

For example, if input is "Yo quiero ir a el parque" your entire output should be just "Yo quiero ir al parque"`;
      }
      
      logger.debug('Transformation prompt:', transformationPrompt);
      
      const token = await user.getIdToken();
      const data = await apiClient.post('/api/chatbot/transform-text', {
        text,
        language: currentLanguage,
        prompt: transformationPrompt,
        politenessMode,
        japaneseSpeechStyle: currentLanguage === 'Japanese' ? japaneseSpeechStyle : 'neutral',
        chineseCharacterType: currentLanguage === 'Chinese' ? chineseCharacterType : 'Simplified'
      }, {
        headers: {
          // 'Authorization': `Bearer ${token}` // Usually handled by apiClient
        }
      });

      logger.debug('Raw transformed text result:', data.transformedText);
      
      logger.debug('Cleaned transformed text:', data.transformedText);
      logger.debug('==== END FLUENT MODE TRANSFORMATION ====');
      
      return data.transformedText || text;
    } catch (error) {
      logger.error('Chatbot - Error transforming text:', error);
      return text; // Fallback to original text on error
    } finally {
      setIsTransformingText(false);
    }
  };

  // Simple validation to check if transformation is related to original text
  const isRelatedToOriginal = (original, transformed, language) => {
    // If they're identical, that's fine
    if (original === transformed) return true;
    
    // Special handling for Japanese language
    if (language === 'Japanese') {
      // For Japanese, we need more flexible validation since transformations may look very different
      
      // First check if input is romaji and output is Japanese characters
      const isRomajiInput = /^[a-zA-Z0-9\s\.\,\!\?]+$/.test(original);
      const hasJapaneseChars = /[\u3000-\u303f\u3040-\u309f\u30a0-\u30ff\uff00-\uff9f\u4e00-\u9faf]/.test(transformed);
      
      // If input was romaji and output has Japanese characters, this is likely valid
      if (isRomajiInput && hasJapaneseChars) {
        return true;
      }
      
      // Check if there are at least some common characters (excluding particles and punctuation)
      // More lenient approach for Japanese
      const originalChars = Array.from(original.replace(/[は|が|を|に|で|と|の|も|や|。|、|！|？]/g, ''));
      const transformedChars = Array.from(transformed.replace(/[は|が|を|に|で|と|の|も|や|。|、|！|？]/g, ''));
      
      // For very short messages, require less overlap since transformations can be substantially different
      if (originalChars.length < 10 || transformedChars.length < 10) {
        const commonChars = originalChars.filter(char => transformedChars.includes(char));
        // Lower the threshold to 10% for short messages
        const overlapRatio = commonChars.length / Math.min(originalChars.length, transformedChars.length);
        return overlapRatio >= 0.1; 
      }
      
      // For longer messages, we need fewer common characters since Japanese transformations can be quite different
      const commonChars = originalChars.filter(char => transformedChars.includes(char));
      return commonChars.length >= 1; // Even just one common character can indicate related content
    }
    
    // Normalize both texts for comparison (lowercase, remove punctuation)
    const normalize = (str) => str.toLowerCase().replace(/[.,\/#!$%\^&\*;:{}=\-_`~()¡¿]/g, '').trim();
    const normalizedOriginal = normalize(original);
    const normalizedTransformed = normalize(transformed);
    
    // Check for minimum length to avoid false positives
    if (normalizedTransformed.length < 2) return false;
    
    // For short texts (greetings, etc.), at least some words should match
    const originalWords = new Set(normalizedOriginal.split(/\s+/));
    const transformedWords = normalizedTransformed.split(/\s+/);
    
    // For short sentences, at least one content word should match
    // These are common words to ignore in comparison (articles, common pronouns)
    const commonWords = {
      'Spanish': ['el', 'la', 'los', 'las', 'un', 'una', 'unos', 'unas', 'y', 'o', 'a', 'de', 'en', 'con', 'por', 'para', 'mi', 'tu', 'su'],
      'French': ['le', 'la', 'les', 'un', 'une', 'des', 'et', 'ou', 'à', 'de', 'en', 'avec', 'pour', 'mon', 'ton', 'son'],
      'Italian': ['il', 'lo', 'la', 'i', 'gli', 'le', 'un', 'uno', 'una', 'e', 'o', 'a', 'di', 'in', 'con', 'per', 'mio', 'tuo', 'suo'],
      'Japanese': ['は', 'が', 'を', 'に', 'で', 'と', 'の', 'も', 'や']
    };
    
    const ignoreWords = commonWords[language] || [];
    const ignoreWordsSet = new Set(ignoreWords);
    
    // Count how many non-common words match
    let matchCount = 0;
    for (const word of transformedWords) {
      if (word.length > 1 && !ignoreWordsSet.has(word) && originalWords.has(word)) {
        matchCount++;
      }
    }
    
    // If it's a greeting or very short sentence, common patterns should match
    const commonGreetings = {
      'Spanish': ['hola', 'buenos', 'buenas', 'como', 'estas', 'que', 'tal', 'adios', 'gracias'],
      'French': ['bonjour', 'salut', 'comment', 'ca', 'va', 'merci', 'au revoir'],
      'Italian': ['ciao', 'buongiorno', 'come', 'stai', 'va', 'grazie', 'arrivederci'],
      'Japanese': ['こんにちは', 'おはよう', 'こんばんは', 'さようなら', 'ありがとう']
    };
    
    // Special case for greetings
    const greetingWords = commonGreetings[language] || [];
    
    // If it looks like a greeting, handle it specially
    let greetingMatch = false;
    for (const word of greetingWords) {
      if (normalizedOriginal.includes(word) && normalizedTransformed.includes(word)) {
        greetingMatch = true;
        break;
      }
    }
    
    // Check for names in the original that should be in the transformed
    const originalTokens = normalizedOriginal.split(/\s+/);
    for (const token of originalTokens) {
      // If there's a capitalized word (likely a name), it should be preserved
      if (token.length > 1 && original.includes(token) && token[0] === token[0].toUpperCase()) {
        if (!normalizedTransformed.includes(token.toLowerCase())) {
          // Missing a name that was in the original
          return false;
        }
      }
    }
    
    // If it contains "bojee", that should be preserved
    if (normalizedOriginal.includes('bojee') && !normalizedTransformed.includes('bojee')) {
      return false;
    }
    
    // Decision criteria
    if (greetingMatch) return true;
    if (matchCount > 0) return true;
    
    // For very short sentences, ensure some character overlap (for languages like Japanese)
    if (normalizedOriginal.length < 10 && normalizedTransformed.length < 20) {
      // For short phrases, check if at least 30% of characters match
      let charMatchCount = 0;
      for (const char of normalizedOriginal) {
        if (normalizedTransformed.includes(char)) {
          charMatchCount++;
        }
      }
      if (charMatchCount / normalizedOriginal.length > 0.3) return true;
    }
    
    // If the transformed is much longer, it's likely not related
    if (normalizedTransformed.length > normalizedOriginal.length * 3) {
      return false;
    }
    
    // Default to false if none of the above checks pass
    return false;
  };

  // Check if trial has expired or subscription is canceled
  const isTrialExpired = () => {
    // Always consider canceled plans as expired
    if (userPlan === 'canceled') return true;
    
    // Check if trial is expired by date
    if (userPlan !== 'trial' || !planExpiresAt) return false;
    const now = new Date();
    const expiration = new Date(planExpiresAt);
    return now > expiration;
  };
  
  // Get appropriate message based on plan status
  const getAccessLimitMessage = () => {
    if (userPlan === 'canceled') {
      return (
        <>
          Your subscription has been canceled. Please <Link 
            onClick={handleShowPricing} 
            to="#" 
                  style={{ color: '#007bff', textDecoration: 'underline', fontWeight: 'bold' }}>
            upgrade
          </Link> to the Premium Plan to use this feature.
        </>
      );
    } else if (userPlan === 'trial' && isTrialExpired()) {
      return (
        <>
          Your trial has ended. Please <Link 
            onClick={handleShowPricing} 
            to="#" 
                  style={{ color: '#007bff', textDecoration: 'underline', fontWeight: 'bold' }}>
            upgrade
              </Link> to the Premium Plan to continue chatting.
        </>
      );
    } else {
      return (
        <>
          You've reached your daily message limit. Please <Link 
            onClick={handleShowPricing} 
            to="#" 
            style={{ color: '#c41c1c', textDecoration: 'underline', fontWeight: 'bold' }}>
            upgrade
          </Link> to Premium for unlimited access.
        </>
      );
    }
  };

  const handleSendMessage = async () => {
    if (!input.trim()) return;
    
    // Block Pro users completely
    if (userPlan === 'pro') {
      logger.debug('Pro user attempted to send a message - blocked');
      return;
    }
    
    // Check for trial expiration or canceled subscription
    if (isTrialExpired()) {
      logger.debug('Trial expired or subscription canceled - blocked message sending');
      return;
    }
    
    // Check message limit for trial users
    if (userPlan === 'trial') {
      const currentMode = chatMode;
      const currentLanguage = settings.learningLanguage;
      
      try {
        const limitInfo = await userActivityService.hasReachedMessageLimit(
          currentLanguage,
          currentMode,
          userPlan
        );
        
        // Update the state with current limits
        setMessageLimit(prev => ({
          ...prev,
          [currentMode]: limitInfo
        }));
        
        // If limit reached, don't allow sending
        if (limitInfo.hasReached) {
          logger.debug(`Message limit reached for ${currentLanguage} in ${currentMode} mode`);
          return;
        }
      } catch (error) {
        logger.error('Error checking message limit before sending:', error);
      }
    }
    
    // Store original input
    const originalInput = input;
    setInput('');
    setIsOverCharLimit(false);
    setCharOverLimit(0);

    // Ensure input focus immediately
    if (inputRef.current) {
      inputRef.current.focus();
    }

    try {
      setIsLoading(true);
      
      logger.debug('==== USER MESSAGE ====');
      logger.debug('User message:', originalInput);
      
      // Transform text if fluent mode is enabled, otherwise use original
      let processedInput = originalInput;
      if (fluentMode) {
        // When using fluent mode, update the state to track it
        // But don't try to modify the constants directly
        if (chatMode === 'bojee') {
          setHasUsedFluentModeInBojee(true);
        } else {
          setHasUsedFluentModeInCustom(true);
        }
        
        processedInput = await transformToFluentLanguage(originalInput);
        logger.debug('==== FLUENT VERSION ====');
        logger.debug('Original:', originalInput);
        logger.debug('Transformed:', processedInput);
        
        // Force save with fluent mode flag true immediately
        // Create a new messages array with the new message
        const newMessages = [...messages, { 
          role: 'user', 
          content: processedInput,
          id: Date.now() 
        }];
        
        // Force save with usedFluentMode=true
        try {
          // Find existing conversation for current mode
          const conversations = await getConversations(user.uid, settings.learningLanguage);
          const existingConversation = conversations?.find(conv => conv.chatMode === chatMode);
          
          if (existingConversation) {
            logger.debug(`Saving ${chatMode} conversation with fluent mode flag = true`);
            
            // Use the MongoDB API directly with usedFluentMode=true
            if (chatMode === 'custom') {
              await saveConversation(
                user.uid, 
                settings.learningLanguage,
                newMessages,
                'custom',
                customName,
                customPrompt,
                politenessMode,
                true // Force usedFluentMode=true
              );
            } else {
              await saveConversation(
                user.uid,
                settings.learningLanguage,
                newMessages,
                'bojee',
                '',
                '',
                politenessMode,
                true // Force usedFluentMode=true  
              );
            }
          } else if (newMessages.length > 0) {
            logger.debug(`Creating new ${chatMode} conversation with fluent mode flag = true`);
            
            await saveConversation(
              user.uid,
              settings.learningLanguage,
              newMessages,
              chatMode,
              chatMode === 'custom' ? customName : '',
              chatMode === 'custom' ? customPrompt : '',
              politenessMode,
              true // Force usedFluentMode=true
            );
          }
          
          logger.debug('Successfully saved conversation with fluent mode = true');
        } catch (error) {
          logger.error('Error saving conversation with fluent mode:', error);
        }
      }
      
      // Only add the processed message to chat (either original or fluent version)
      const userMessage = { 
        role: 'user', 
        content: processedInput,
        id: Date.now()
      };
      
      // Add message to chat
      setMessages(prev => [...prev, userMessage]);
      
      // Record message sent for trial users
      if (isAuthenticated && user) {
        try {
          const currentLanguage = settings.learningLanguage;
          const currentMode = chatMode;
          
          logger.debug(`Attempting to record message for ${currentLanguage} in ${currentMode} mode with user plan: ${userPlan}`);
          
          // Record the message in user activity
          const result = await userActivityService.recordChatMessage(currentLanguage, currentMode);
          logger.debug(`Recorded message for ${currentLanguage} in ${currentMode} mode:`, result);
          
          // Immediately update UI with the returned count from the server
          if (result && typeof result.messageCount === 'number') {
            logger.debug(`Setting message count directly from API response: ${result.messageCount}`);
            setMessageLimit(prev => ({
              ...prev,
              [currentMode]: {
                ...(prev[currentMode] || {}),
                count: result.messageCount,
                limit: prev[currentMode]?.limit || 25,
                hasReached: result.messageCount >= (prev[currentMode]?.limit || 25)
              }
            }));
          } else {
            // Fallback to requesting an update if the count wasn't returned
            const updatedLimitInfo = await userActivityService.hasReachedMessageLimit(
              currentLanguage,
              currentMode,
              userPlan
            );
            
            logger.debug(`Got updated limit info:`, updatedLimitInfo);
            
            setMessageLimit(prev => ({
              ...prev,
              [currentMode]: updatedLimitInfo
            }));
          }
        } catch (error) {
          logger.error('Error recording chat message:', error);
          // Still try to get updated counts even if recording failed
          try {
            const updatedLimitInfo = await userActivityService.hasReachedMessageLimit(
              settings.learningLanguage,
              chatMode,
              userPlan
            );
            setMessageLimit(prev => ({
              ...prev,
              [chatMode]: updatedLimitInfo
            }));
          } catch (refreshError) {
            logger.error('Error refreshing message limit data:', refreshError);
          }
        }
      }
      
      // Get the current language, always ensure we have a valid language
      const currentLanguage = settings.learningLanguage || 'Spanish';
      const proficiencyLevel = settings.languageLevel || 'Beginner';
      const spanishRegion = settings.spanishRegion || 'Latin America';
      logger.debug(`Preparing request: Mode=${chatMode}, Language=${currentLanguage}, Level=${proficiencyLevel}${currentLanguage === 'Spanish' ? `, Region=${spanishRegion}` : ''}`);
      
      // Get Japanese speech style if the language is Japanese
      const japaneseSpeechStyle = currentLanguage === 'Japanese' 
        ? localStorage.getItem('japaneseSpeechStyle') || 'neutral'
        : 'neutral';
        
      if (currentLanguage === 'Japanese') {
        logger.debug(`Japanese speech style: ${japaneseSpeechStyle}`);
      }
      
      // Get Chinese character type if the language is Chinese
      const chineseCharacterType = currentLanguage === 'Chinese'
        ? localStorage.getItem('chineseCharacterType') || 'Simplified'
        : 'Simplified';
        
      if (currentLanguage === 'Chinese') {
        logger.debug(`Chinese character type: ${chineseCharacterType}`);
      }
      
      // System prompt always goes first in the array
      const systemPrompt = getSystemPrompt(
        chatMode, 
        currentLanguage, 
        proficiencyLevel, 
        spanishRegion,
        chatMode === 'custom' ? customPrompt : '',
        customName || 'Bojee',
        politenessMode,
        japaneseSpeechStyle,
        chineseCharacterType
      );
      
      // Add memory to the system prompt if available - only in bojee mode
      let fullSystemPrompt = systemPrompt;
      if (isAuthenticated && user && chatMode === 'bojee') {
        try {
          // Check if memory is enabled in settings using our helper
          if (isMemoryEnabled()) {
            logger.debug('Chatbot - Adding memory facts to system prompt');
            
            // Get all memory facts - using the async version
            const memoryData = await chatMemoryManager.getMemoryFacts(user.uid, currentLanguage);
            
            if (memoryData && memoryData.length > 0) {
              logger.debug(`Chatbot - Adding ${memoryData.length} memory facts to system prompt`);
              
              // Format memory facts as bullet points
              const formattedMemory = memoryData.map(fact => `- ${fact}`).join('\n');
              
              // Add memory facts to the system prompt
              fullSystemPrompt = `${systemPrompt}\n\nNOTABLE FACTS ABOUT THE USER:\n${formattedMemory}`;
            }
          } else {
            logger.debug('Chatbot - Memory is disabled in settings, skipping memory facts in prompt');
          }
        } catch (error) {
          logger.error('Error getting memory facts:', error);
        }
      }
      
      // Log the system prompt being used
      logger.debug('==== SYSTEM PROMPT ====');
      logger.debug(`System prompt (${chatMode} mode for ${currentLanguage}):`, fullSystemPrompt);
      logger.debug('==== END SYSTEM PROMPT ====');

      // Create the contents array for the API request
      const contents = [
        {
          role: 'system',
          parts: [{ text: fullSystemPrompt }]
        }
      ];
      
      // Add conversation history (excluding the message we just added)
      logger.debug(`Adding ${messages.length} previous messages to conversation history`);
      
      // Include all existing messages in the conversation
      messages.forEach((msg) => {
        contents.push({
          role: msg.role,
          parts: [{ text: msg.content }]
        });
      });
      
      // Add current user message
      contents.push({
        role: "user",
        parts: [{ text: processedInput }]
      });

      // Make API request to Gemini
      logger.debug('==== SENDING TO GEMINI ====');
      logger.debug('Final input text being sent to Gemini:', processedInput);
      const token = await user.getIdToken();
      
      const requestBody = {
        model: "gemini-2.0-flash",
        contents,
        generationConfig: {
          temperature: 0.9,
          topP: 0.95,
          topK: 40,
          maxOutputTokens: 1024,
          responseMimeType: "text/plain",
        },
        chatMode: chatMode,
        language: currentLanguage
      };
      
      // Add language-specific parameters
      if (currentLanguage === 'Japanese') {
        requestBody.japaneseSpeechStyle = japaneseSpeechStyle;
      }
      
      if (currentLanguage === 'Chinese') {
        requestBody.chineseCharacterType = chineseCharacterType;
      }
      
      logger.debug('Request structure prepared with', contents.length, 'message(s)');
      
      // Use apiClient for the request
      const data = await apiClient.post('/api/chatbot', requestBody, {
        headers: {
          // Authorization is handled by apiClient, but if you need to override or add other specific headers:
          // 'Authorization': `Bearer ${token}` // This is usually not needed with apiClient
        }
      });

      logger.debug('==== GEMINI RESPONSE ====');
      logger.debug('Full response from Gemini:', data.text);
      
      // Filter out any "NOTABLE FACTS ABOUT THE USER" section that might appear in the response
      let cleanedContent = data.visibleText || data.text;
      
      // Remove any "NOTABLE FACTS ABOUT THE USER" section
      const notableFactsRegex = /NOTABLE FACTS ABOUT THE USER:[\s\S]*?(?=\n\n|\n$|$)/;
      cleanedContent = cleanedContent.replace(notableFactsRegex, '').trim();
      
      // Add assistant response to chat
      setMessages(prev => [...prev, { 
        role: 'assistant', 
        content: cleanedContent,
        fullResponse: data.text,
        id: Date.now()
      }]);

      // Only process and save memory facts in learning (Bojee) mode
      if (chatMode === 'bojee' && data.memory && Array.isArray(data.memory) && data.memory.length > 0) {
        logger.debug('Chatbot - Received memory facts from API in Bojee mode:', data.memory);
        
        // Add new facts to the universal memory if memory is enabled
        if (isAuthenticated && user) {
          try {
            // Use our centralized helper to check memory status
            if (isMemoryEnabled()) {
              logger.debug('Chatbot - Proceeding to store new memory facts');
              const updatedMemory = await chatMemoryManager.addFacts(user.uid, data.memory);
              
              // Safely handle the response - might be array of objects or simple array of strings
              if (Array.isArray(updatedMemory)) {
                if (updatedMemory.length > 0 && typeof updatedMemory[0] === 'object' && updatedMemory[0].fact) {
                  // If it's an array of objects with fact property
                  setMemoryFacts(updatedMemory.map(item => item.fact));
                } else {
                  // If it's just an array of strings
                  setMemoryFacts(updatedMemory);
                }
                logger.debug(`Chatbot - Updated memory with ${data.memory.length} new facts. Total facts: ${updatedMemory.length}`);
              } else {
                logger.debug('Chatbot - Memory updated but returned format is not as expected:', updatedMemory);
              }
            } else {
              logger.debug('Chatbot - Memory is disabled in settings, skipping memory storage from chat');
            }
          } catch (memoryError) {
            logger.error('Error updating memory:', memoryError);
            // Don't let memory errors stop the conversation
          }
        }
      }
      
      // After successfully sending the message, if in custom mode, explicitly save the current
      // custom name and prompt to MongoDB to ensure they're updated
      if (chatMode === 'custom' && isAuthenticated && user && settings.learningLanguage) {
        try {
          logger.debug('Saving custom name and prompt after sending message');
          const conversations = await getConversations(user.uid, settings.learningLanguage);
          const existingConversation = conversations?.find(conv => conv.chatMode === 'custom');
          
          if (existingConversation) {
            // We'll update after the response is received to include it
          } else {
            // Create a new conversation with current settings
            await saveConversation(
              user.uid,
              settings.learningLanguage,
              [...messages, userMessage], // Include the user message we just sent
              'custom',
              customName,
              customPrompt,
              politenessMode
            );
          }
        } catch (error) {
          logger.error('Error saving custom settings:', error);
        }
      }
      
      // After receiving the response, explicitly save everything to MongoDB if in custom mode
      if (chatMode === 'custom' && isAuthenticated && user && settings.learningLanguage) {
        try {
          const updatedMessages = [...messages, userMessage, { 
            role: 'assistant', 
            content: cleanedContent
          }];
          
          const conversations = await getConversations(user.uid, settings.learningLanguage);
          const existingConversation = conversations?.find(conv => conv.chatMode === 'custom');
          
          if (existingConversation) {
            // Update the existing conversation with all current data
            await saveConversation(
              user.uid,
              settings.learningLanguage,
              updatedMessages,
              'custom',
              customName,
              customPrompt,
              politenessMode
            );
            logger.debug('Updated custom conversation with new messages, name and prompt');
          }
        } catch (error) {
          logger.error('Error saving custom conversation after response:', error);
        }
      }
      
      logger.debug('==== END OF CONVERSATION TURN ====');
    } catch (error) {
      logger.error('Error sending message:', error);
      setMessages(prev => [...prev, { 
        role: 'assistant', 
        content: 'Sorry, I encountered an error. Please try again later.',
        id: Date.now()
      }]);
    } finally {
      setIsLoading(false);
      
      // Ensure focus is maintained after loading finishes with multiple approaches for reliability
      if (inputRef.current) {
        inputRef.current.focus();
        
        // Also use a delayed focus as a fallback
        setTimeout(() => {
          if (inputRef.current) {
            inputRef.current.focus();
          }
        }, 50);
      }
    }
  };

  const handleKeyDown = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSendMessage();
    }
  };

  // Handle mode change
  const handleModeChange = (e) => {
    const newMode = e.target.value;
    
    if (chatMode === newMode) return; // No change
    
    logger.debug(`Switching from ${chatMode} to ${newMode} mode`);
    
    // Save current messages to the appropriate store
    if (chatMode === 'bojee' && JSON.stringify(bojeeMessages) !== JSON.stringify(messages)) {
      setBojeeMessages([...messages]);
    } else if (chatMode === 'custom' && JSON.stringify(customMessages) !== JSON.stringify(messages)) {
      setCustomMessages([...messages]);
    }
    
    // Load messages from the appropriate store for the new mode
    if (newMode === 'bojee') {
      setMessages(bojeeMessages.length > 0 ? [...bojeeMessages] : []);
    } else {
      setMessages(customMessages.length > 0 ? [...customMessages] : []);
    }
    
    // Turn off fluent mode when switching modes
    setFluentMode(false);
    
    // Update the mode
    setChatMode(newMode);
    
    // Update the document title
    document.title = newMode === 'custom' && customName ? `Chat with ${customName}` : 'Chat with Bojee';
  };

  // Handle custom prompt changes
  const handleCustomPromptChange = (e) => {
    // Only update state, don't trigger MongoDB save
    setCustomPrompt(e.target.value);
  };
  
  // Handler for custom name changes
  const handleCustomNameChange = (e) => {
    const newName = e.target.value;
    // Only update state, don't trigger MongoDB save
    setCustomName(newName);
    
    // Update the page title with the new name if in custom mode
    if (chatMode === 'custom') {
      document.title = newName ? `Chat with ${newName}` : 'Chat with Bojee';
    }
  };

  // Add a function to toggle translation for a specific message
  const toggleTranslation = async (index, message) => {
    logger.debug(`Chatbot - Toggling translation for message ${index}`);
    
    // If already translated, just toggle it back to original
    if (translatedMessages[index]) {
      setTranslatedMessages(prev => ({
        ...prev,
        [index]: false
      }));
      return;
    }
    
    // For the welcome message (index 0), we already have the English translation
    if (index === 0 && message.englishTranslation) {
      setTranslatedMessages(prev => ({
        ...prev,
        [index]: true
      }));
      return;
    }
    
    // Check if we already have a cached translation for this message
    if (message.translation) {
      // Use the cached translation instead of fetching again
      setTranslatedMessages(prev => ({
        ...prev,
        [index]: true
      }));
      return;
    }
    
    // For other AI messages, we need to fetch a translation from Gemini
    if (message.role === 'assistant') {
      try {
        logger.debug(`Chatbot - Fetching translation for message ${index}`);
        
        // Show loading state for this message
        setMessages(prev => {
          const updatedMessages = [...prev];
          updatedMessages[index] = {
            ...updatedMessages[index],
            isTranslating: true
          };
          return updatedMessages;
        });
        
        // Set this message as translated to prevent multiple fetches
        setTranslatedMessages(prev => ({
          ...prev,
          [index]: true
        }));
        
        // Get the current language
        const currentLanguage = settings.learningLanguage || 'Spanish';
        
        // Get authentication token
        const token = await user.getIdToken();
        
        // Make the API request to translate the text
        const data = await apiClient.post('/api/chatbot/translate', {
          text: message.content,
          sourceLanguage: currentLanguage
        }, {
          headers: {
            // 'Authorization': `Bearer ${token}` // Usually handled by apiClient
          }
        });

        logger.debug('Chatbot - Translation received:', data.translation.substring(0, 50) + '...');
        
        // Update the message with the translation
        setMessages(prev => {
          const updatedMessages = [...prev];
          updatedMessages[index] = {
            ...updatedMessages[index],
            translation: data.translation,
            isTranslating: false
          };
          return updatedMessages;
        });
      } catch (error) {
        logger.error('Chatbot - Error translating message:', error);
        
        // Remove loading state
        setMessages(prev => {
          const updatedMessages = [...prev];
          updatedMessages[index] = {
            ...updatedMessages[index],
            isTranslating: false
          };
          return updatedMessages;
        });
        
        // Revert translation state since we couldn't get a translation
        setTranslatedMessages(prev => ({
          ...prev,
          [index]: false
        }));
        
        // Show error to user
        alert(`Failed to translate message: ${error.message}`);
      }
    } else {
      // Toggle the translation state for non-assistant messages
      setTranslatedMessages(prev => ({
        ...prev,
        [index]: true
      }));
    }
  };

  // Function to toggle dictionary accordion and ensure only one accordion is open
  const toggleDictionary = () => {
    if (dictionaryOpen) {
      setDictionaryOpen(false);
      setActiveOverlay(null);
    } else {
      setDictionaryOpen(true);
    }
  };

  // Function to toggle chat mode accordion and ensure only one accordion is open
  const toggleChatMode = () => {
    setActiveOverlay(activeOverlay === 'chatMode' ? null : 'chatMode');
  };

  // Function to toggle politeness mode accordion and ensure only one accordion is open
  const togglePolitenessMode = () => {
    setActiveOverlay(activeOverlay === 'politenessMode' ? null : 'politenessMode');
  };

  // New function to close any active overlay
  const closeOverlay = () => {
    setActiveOverlay(null);
  };

  // Function to handle selecting a word for the dictionary panel
  const handleWordSelect = (word, sentence) => {
    setSelectedWord(word);
    setSelectedSentence(sentence);
    setDictionaryOpen(true);
    setActiveOverlay('dictionary');
    
    // Remove the problematic tracking code
    // if (isAuthenticated && user) {
    //   userActivityService.trackUserActivity(user.uid, 'word_lookup', { word, language: settings.learningLanguage });
    // }
  };

  // Handle input change with character limit warning
  const handleInputChange = (e) => {
    const newValue = e.target.value;
    setInput(newValue);
    
    // Check if over character limit
    if (newValue.length > CHARACTER_LIMIT) {
      setIsOverCharLimit(true);
      setCharOverLimit(newValue.length - CHARACTER_LIMIT);
    } else {
      setIsOverCharLimit(false);
      setCharOverLimit(0);
    }
  };

  // Function to get the message content with clickable words for Bojee's messages
  const getMessageContent = (message, index) => {
    // Special rendering for evaluation messages
    if (message.isEvaluation) {
      return (
        <div className="evaluation-message">
          <h3>Your Language Evaluation</h3>
          <div className="evaluation-content">
            {message.content}
          </div>
        </div>
      );
    }
    
    // If message is currently being translated, show loading indicator
    if (message.isTranslating) {
      return <div className="translating-message">Translating<span>.</span><span>.</span><span>.</span></div>;
    }
    
    // If it's a translation and we have the translation data available
    if (translatedMessages[index] && (message.translation || message.englishTranslation)) {
      // Return the translation directly without any special styling
      return message.translation || message.englishTranslation;
    } else if (translatedMessages[index] && !message.translation && !message.englishTranslation) {
      // We're trying to show a translation but don't have it yet - likely a race condition
      // Fetch the translation if it's an assistant message
      if (message.role === 'assistant' && !message.isTranslating) {
        // Trigger translation fetch
        toggleTranslation(index, message);
        return <div className="translating-message">Translating<span>.</span><span>.</span><span>.</span></div>;
      }
      return 'Translation not available';
    }

    // Get the content
    const content = message.content;
    
    // For regular messages
    if (content) {
      // Process the content with word lookup if needed
      if (settings.learningLanguage) {
        try {
          return processChatbotSentence(content, settings.learningLanguage, handleWordSelect);
        } catch (error) {
          logger.error('Error processing sentence:', error);
          return content;
        }
      }
      return content;
    }
    
    return '';
  };

  // Cleanup: Save any unsaved memory when component unmounts
  useEffect(() => {
    return () => {
      if (isAuthenticated && user) {
        logger.debug('Chatbot - Component unmounting, memory is auto-saved via addFacts');
      }
    };
  }, [isAuthenticated, user]);

  // Helper function to check if memory is enabled
  const isMemoryEnabled = () => {
    const memoryEnabled = localStorage.getItem('memoryEnabled') === 'true';
    logger.debug(`Chatbot - Memory feature check: ${memoryEnabled ? 'ENABLED' : 'DISABLED'}`);
    return memoryEnabled;
  };

  // Toggle showing memory debug panel
  const toggleMemoryDebug = () => {
    const newState = !showMemoryDebug;
    setShowMemoryDebug(newState);
    
    if (newState && isAuthenticated && user) {
      // When showing debug panel, refresh memory data - using async getMemory
      const refreshMemoryData = async () => {
        // Only fetch memory data if memory is enabled
        if (isMemoryEnabled()) {
          logger.debug('Chatbot - Memory debug panel: Fetching memory data because memory is enabled');
          const memory = await chatMemoryManager.getMemory(user.uid);
          setMemoryFacts(memory.map(item => item.fact));
        } else {
          logger.debug('Chatbot - Memory debug panel: Memory is disabled, not fetching data');
          setMemoryFacts([]);
        }
      };
      
      refreshMemoryData();
    }
  };

  // Toggle fluent mode
  const toggleFluentMode = async () => {
    // Block Pro users
    if (userPlan === 'pro') {
      logger.debug('Pro user attempted to toggle fluent mode - blocked');
      return;
    }
    
    const newFluentMode = !fluentMode;
    setFluentMode(newFluentMode);
    
    // We're no longer saving to MongoDB here - only when sending a message
    // with fluent mode actually enabled
  };

  // Load evaluation count when settings are loaded
  useEffect(() => {
    if (settingsLoaded && settings.learningLanguage) {
      checkEvaluationLimit();
    }
  }, [settings.learningLanguage, settingsLoaded]);

  // Check if user has reached evaluation limit for today
  const checkEvaluationLimit = async () => {
    try {
      const canRequestEval = await userActivityService.canRequestEvaluation(settings.learningLanguage);
      if (!canRequestEval) {
        const activity = await userActivityService.getUserActivity();
        const count = activity.reloadCounts?.evaluations?.[settings.learningLanguage] || 0;
        setEvaluationCount(count);
      } else {
        const activity = await userActivityService.getUserActivity();
        const count = activity.reloadCounts?.evaluations?.[settings.learningLanguage] || 0;
        setEvaluationCount(count);
      }
      setIsEvaluationLimitChecked(true);
    } catch (error) {
      logger.error('Error checking evaluation limit:', error);
      setIsEvaluationLimitChecked(true); // Still mark as checked to prevent infinite retries
    }
  };

  const handleRequestEvaluation = async () => {
    if (isEvaluating || !messages.length) return;
    
    // Block Pro users
    if (userPlan === 'pro') {
      logger.debug('Pro user attempted to request evaluation - blocked');
      return;
    }
    
    // Check if fluent mode has been used in the current mode
    const hasUsedFluentModeInCurrentMode = 
      chatMode === 'bojee' ? hasUsedFluentModeInBojee : hasUsedFluentModeInCustom;
    
    // If fluent mode has been used in this mode, don't allow evaluation
    if (hasUsedFluentModeInCurrentMode) {
      logger.debug(`Evaluation disabled because fluent mode was used in ${chatMode} mode`);
      return;
    }
    
    // Check if user has already reached the evaluation limit
    if (evaluationCount >= maxEvaluations) {
      logger.debug('Evaluation limit reached for today');
      return;
    }
    
    try {
      setIsEvaluating(true);
      logger.debug('Requesting language evaluation');
      
      // Record the evaluation usage first
      const evalResult = await userActivityService.recordEvaluation(settings.learningLanguage);
      
      // If we got a 403, the user has reached the limit
      if (evalResult.message === 'Maximum evaluations reached for today') {
        setEvaluationCount(evalResult.evaluationCount);
        setIsEvaluating(false);
        return;
      }
      
      // Update the count
      setEvaluationCount(evalResult.evaluationCount);
      
      const token = await user.getIdToken();
      const data = await apiClient.post('/api/chatbot/evaluate', {
        chatHistory: messages,
        language: settings.learningLanguage
      }, {
        headers: {
          // 'Authorization': `Bearer ${token}` // Usually handled by apiClient
        }
      });

      logger.debug('Evaluation received:', data.evaluation);
      
      // Format the evaluation result from Gemini
      let formattedEvaluation = data.evaluation;
      
      // Check if there's a "FINAL EVALUATION:" section and keep only that part
      const finalEvalRegex = /FINAL EVALUATION:([\s\S]*)/i;
      const finalEvalMatch = data.evaluation.match(finalEvalRegex);
      
      if (finalEvalMatch && finalEvalMatch[1]) {
        formattedEvaluation = finalEvalMatch[1].trim();
      }
      
      // Add the evaluation as a bot message
      setMessages(prevMessages => [
        ...prevMessages,
        {
          role: 'assistant',
          content: formattedEvaluation,
          isEvaluation: true
        }
      ]);
      
      // Store the evaluation result
      setEvaluation(formattedEvaluation);
    } catch (error) {
      logger.error('Error evaluating language skills:', error);
      // alert('Failed to evaluate your language skills. Please try again.'); // Old alert
      setMessages(prev => [...prev, { 
        role: 'assistant', 
        content: 'Sorry, I couldn\'t evaluate your language skills at this time. Please try again later.',
        id: Date.now(),
        isError: true // Optional: flag for styling error messages differently
      }]);
    } finally {
      setIsEvaluating(false);
    }
  };

  // Handle tips popup open/close
  const handleTipsClick = () => {
    setShowTips(true);
  };

  // Handle tips popup dismissal
  const handleTipsDismiss = () => {
    setIsClosing(true);
    // Delay the actual closing to allow for the animation to play
    setTimeout(() => {
      setShowTips(false);
      setTipsRead(true);
      setIsClosing(false);
      localStorage.setItem("chatbotTipsRead", "true");
    }, 300); // Match this to the animation duration
  };

  // Handle new conversation
  const handleNewConversation = async () => {
    if (!isAuthenticated || !user) return;
    
    logger.debug('Creating new conversation');
    
    // Set loading state
    setIsCreatingNewConversation(true);
    
    try {
      // Clear UI messages immediately for responsiveness
      setMessages([]);
      
      // Save empty messages to current mode in MongoDB
      // We need to create/update the conversation to reset it properly
      await saveToMongoDB([]);
      
      // Reset current conversation ID
      setCurrentConversationId(null);
      
      // Reset fluent mode and its usage flags
      setFluentMode(false);
      
      // Reset the fluent mode usage flags for the current mode
      if (chatMode === 'bojee') {
        setHasUsedFluentModeInBojee(false);
      } else {
        setHasUsedFluentModeInCustom(false);
      }
      
      // Update cache for current mode
        if (chatMode === 'bojee') {
        setBojeeMessages([]);
        localStorage.setItem(`chatbot_bojee_messages_${user.uid}_${settings.learningLanguage}`, JSON.stringify([]));
        } else {
        setCustomMessages([]);
        localStorage.setItem(`chatbot_custom_messages_${user.uid}_${settings.learningLanguage}`, JSON.stringify([]));
      }
      
      // Also update cached mode
      localStorage.setItem(`chatbot_mode_${user.uid}_${settings.learningLanguage}`, chatMode);
      
      // Mark as new conversation
      setIsNewConversation(true);
      
      logger.debug('Successfully created new conversation');
    } catch (error) {
      logger.error('Error creating new conversation:', error);
    } finally {
      setIsCreatingNewConversation(false);
    }
  };

  // Handle message edit start
  const handleEditStart = (index) => {
    if (messages[index].role === 'user') {
      setEditingMessageIndex(index);
      setEditedMessageContent(messages[index].content);
    }
  };

  // Handle message edit cancel
  const handleEditCancel = () => {
    setEditingMessageIndex(null);
    setEditedMessageContent('');
  };

  // Handle message edit save
  const handleEditSave = async (index) => {
    if (!editedMessageContent.trim()) return;

    // Store original messages to revert if needed
    const originalMessages = [...messages];
    
    try {
      // Update the message content
      const updatedMessages = [...messages];
      updatedMessages[index] = {
        ...updatedMessages[index],
        content: editedMessageContent
      };
      
      // If the next message is from the assistant, we need to regenerate the response
      const needsResponseUpdate = index < messages.length - 1 && messages[index + 1].role === 'assistant';
      
      // Update UI messages immediately
      setMessages(updatedMessages);
      setEditingMessageIndex(null);
      setEditedMessageContent('');
      
      // Setup for regenerating the assistant's response
      if (needsResponseUpdate) {
        setIsLoading(true);
        
        // Get all messages up to and including the edited one
        const messagesUpToEdit = updatedMessages.slice(0, index + 1);

        // Get the current language
        const currentLanguage = settings.learningLanguage || 'Spanish';
        const proficiencyLevel = settings.languageLevel || 'Beginner';
        const spanishRegion = settings.spanishRegion || 'Latin America';
        
        // Get Japanese speech style if the language is Japanese
        const japaneseSpeechStyle = currentLanguage === 'Japanese' 
          ? localStorage.getItem('japaneseSpeechStyle') || 'neutral'
          : 'neutral';
        
        // Get Chinese character type if the language is Chinese
        const chineseCharacterType = currentLanguage === 'Chinese'
          ? localStorage.getItem('chineseCharacterType') || 'Simplified'
          : 'Simplified';
        
        // System prompt always goes first
        const systemPrompt = getSystemPrompt(
          chatMode, 
          currentLanguage, 
          proficiencyLevel, 
          spanishRegion,
          chatMode === 'custom' ? customPrompt : '',
          customName || 'Bojee',
          politenessMode,
          japaneseSpeechStyle,
          chineseCharacterType
        );
        
        // Add memory to the system prompt if available - only in bojee mode
        let fullSystemPrompt = systemPrompt;
        if (isAuthenticated && user && chatMode === 'bojee') {
          try {
            if (isMemoryEnabled()) {
              logger.debug('Chatbot - Adding memory facts to system prompt for edit response');
              const memoryData = await chatMemoryManager.getMemoryFacts(user.uid, currentLanguage);
              
              if (memoryData && memoryData.length > 0) {
                const formattedMemory = memoryData.map(fact => `- ${fact}`).join('\n');
                fullSystemPrompt = `${systemPrompt}\n\nNOTABLE FACTS ABOUT THE USER:\n${formattedMemory}`;
              }
            }
          } catch (error) {
            logger.error('Error getting memory facts:', error);
          }
        }
        
        // Create contents array for API request
        const contents = [
          {
            role: 'system',
            parts: [{ text: fullSystemPrompt }]
          }
        ];
        
        // Add all messages up to and including the edited one
        messagesUpToEdit.forEach((msg) => {
          contents.push({
            role: msg.role,
            parts: [{ text: msg.content }]
          });
        });
        
        // Make API request to Gemini
        logger.debug('==== SENDING TO GEMINI AFTER EDIT ====');
        const token = await user.getIdToken(); // apiClient might handle this, but keeping it doesn't hurt if other logic depends on it.
        
        const requestBody = {
          model: "gemini-2.0-flash",
          contents,
          generationConfig: {
            temperature: 0.9,
            topP: 0.95,
            topK: 40,
            maxOutputTokens: 1024,
            responseMimeType: "text/plain",
          },
          chatMode: chatMode,
          language: currentLanguage
        };
        
        // Add language-specific parameters
        if (currentLanguage === 'Japanese') {
          requestBody.japaneseSpeechStyle = japaneseSpeechStyle;
        }
        
        if (currentLanguage === 'Chinese') {
          requestBody.chineseCharacterType = chineseCharacterType;
        }
        
        // Replace fetch with apiClient.post
        // apiClient handles JSON stringification, Content-Type, Authorization, and response parsing.
        const data = await apiClient.post('/api/chatbot', requestBody);
        
        // Filter out any "NOTABLE FACTS ABOUT THE USER" section
        let cleanedContent = data.visibleText || data.text;
        const notableFactsRegex = /NOTABLE FACTS ABOUT THE USER:[\s\S]*?(?=\n\n|\n$|$)/;
        cleanedContent = cleanedContent.replace(notableFactsRegex, '').trim();
        
        // Create updated messages array with new response
        const finalMessages = [...messagesUpToEdit, { 
          role: 'assistant', 
          content: cleanedContent,
          fullResponse: data.text,
          id: Date.now()
        }];
        
        // Add any remaining messages that were after the response
        if (index + 2 < originalMessages.length) {
          finalMessages.push(...originalMessages.slice(index + 2));
        }
        
        setMessages(finalMessages);
        
        // Update the appropriate messages store based on chat mode
        if (chatMode === 'bojee') {
          setBojeeMessages(finalMessages);
        } else {
          setCustomMessages(finalMessages);
        }
        
        // Process memory facts if in Bojee mode
        if (chatMode === 'bojee' && data.memory && Array.isArray(data.memory) && data.memory.length > 0) {
          logger.debug('Chatbot - Received memory facts from API after edit:', data.memory);
          
          // Add new facts to memory if enabled
          if (isAuthenticated && user && isMemoryEnabled()) {
            try {
              const updatedMemory = await chatMemoryManager.addFacts(user.uid, data.memory);
              if (Array.isArray(updatedMemory)) {
                if (updatedMemory.length > 0 && typeof updatedMemory[0] === 'object' && updatedMemory[0].fact) {
                  setMemoryFacts(updatedMemory.map(item => item.fact));
                } else {
                  setMemoryFacts(updatedMemory);
                }
              }
            } catch (error) {
              logger.error('Error updating memory after edit:', error);
            }
          }
        }
      }
      
      // Save to MongoDB
      if (isAuthenticated && user && settings.learningLanguage) {
        saveToMongoDB(updatedMessages);
      }
    } catch (error) {
      logger.error('Error handling edit:', error);
      // Revert to original messages if there was an error
      setMessages(originalMessages);
    } finally {
      setIsLoading(false);
    }
  };

  // Handle message delete
  const handleDeleteMessage = async (index) => {
    try {
      // Remove the message at index
      const updatedMessages = [...messages];
      
      // If deleting a user message followed by an assistant message, remove both
      if (messages[index].role === 'user' && 
          index + 1 < messages.length && 
          messages[index + 1].role === 'assistant') {
        updatedMessages.splice(index, 2);
      } else {
        // Otherwise just remove the one message
        updatedMessages.splice(index, 1);
      }
      
      // Update the message array
      setMessages(updatedMessages);
      
      // Update the appropriate messages store based on chat mode
      if (chatMode === 'bojee') {
        setBojeeMessages(updatedMessages);
      } else {
        setCustomMessages(updatedMessages);
      }
      
      // Save to MongoDB
      if (isAuthenticated && user && settings.learningLanguage) {
        saveToMongoDB(updatedMessages);
      }
    } catch (error) {
      logger.error('Error deleting message:', error);
    }
  };

  // Extract the MongoDB save logic to a reusable function
  const saveToMongoDB = async (messagesToSave) => {
    const saveFnTimestamp = Date.now();
    // Capture current state values at the moment this function is called.
    const currentIsDbLoading = isDbLoading;
    const currentIsAuthenticated = isAuthenticated;
    const currentHasUser = !!user;
    const currentLang = settings.learningLanguage;
    const currentSettingsLoaded = settingsLoaded;
    const messagesToSaveLength = messagesToSave?.length;

    logger.info(`saveToMongoDB FUNCTION CALLED at ${saveFnTimestamp}. messagesToSave.length: ${messagesToSaveLength}`, {
      isDbLoading: currentIsDbLoading,
      isAuthenticated: currentIsAuthenticated,
      hasUser: currentHasUser,
      lang: currentLang,
      settingsLoaded: currentSettingsLoaded,
    });

    if (currentIsDbLoading || !currentIsAuthenticated || !currentHasUser || !currentLang || messagesToSave === undefined || !currentSettingsLoaded) {
      logger.warn(`saveToMongoDB at ${saveFnTimestamp}: ABORTING save inside function. Conditions:`, {
          isDbLoading: currentIsDbLoading,
          isAuthenticated: currentIsAuthenticated,
          hasUser: currentHasUser,
          lang: currentLang,
          messagesToSaveDefined: messagesToSave !== undefined,
          settingsLoaded: currentSettingsLoaded
      });
      return;
    }

    logger.info(`saveToMongoDB at ${saveFnTimestamp}: PROCEEDING with API call. lang: ${currentLang}, mode: ${chatMode}, messages: ${messagesToSaveLength}`);
    try {
      const currentUsedFluentMode = chatMode === 'bojee' ? hasUsedFluentModeInBojee : hasUsedFluentModeInCustom;
      const currentCustomName = chatMode === 'custom' ? customName : '';
      const currentCustomPrompt = chatMode === 'custom' ? customPrompt : '';

      await saveConversation(
        user.uid,
        currentLang, // Use captured currentLang
        messagesToSave,
        chatMode,
        currentCustomName,
        currentCustomPrompt,
        politenessMode,
        currentUsedFluentMode
      );
      logger.info(`saveToMongoDB at ${saveFnTimestamp}: Conversation for ${chatMode} saved successfully via API.`);
    } catch (error) {
      logger.error(`saveToMongoDB at ${saveFnTimestamp}: Error saving conversation to MongoDB:`, error);
    }
  };

  // Handle politeness mode change
  const handlePolitenessModeChange = (e) => {
    const newMode = e.target.value;
    setPolitenessMode(newMode);
  };

  // Function to determine maxEvaluations based on userPlan
  const getPlanEvaluationLimit = () => {
    // All user plans have the same limit of 2 evaluations
    return 2;
  };
  
  // Update maxEvaluations when userPlan changes
  useEffect(() => {
    setMaxEvaluations(getPlanEvaluationLimit());
  }, [userPlan]);

  // Check message limits for trial users
  useEffect(() => {
    const checkMessageLimits = async () => {
      if (!isAuthenticated || !user) return;
      
      // Only check limits for trial users - premium users should not see the limit
      if (userPlan !== 'trial') {
        // Make sure message limits are reset for non-trial users
        setMessageLimit({
          bojee: { count: 0, limit: Infinity, hasReached: false },
          custom: { count: 0, limit: Infinity, hasReached: false }
        });
        return;
      }
      
      if (!settings.learningLanguage) return;
      
      setIsCheckingMessageLimit(true);
      
      try {
        logger.debug('Checking message limits for language:', settings.learningLanguage);
        
        // Get message limits for both modes
        const bojeeLimitInfo = await userActivityService.hasReachedMessageLimit(
          settings.learningLanguage,
          'bojee',
          userPlan
        );
        
        const customLimitInfo = await userActivityService.hasReachedMessageLimit(
          settings.learningLanguage,
          'custom',
          userPlan
        );
        
        logger.debug(`Message limits - Bojee: ${bojeeLimitInfo.count}/${bojeeLimitInfo.limit}, Custom: ${customLimitInfo.count}/${customLimitInfo.limit}`);
        
        setMessageLimit({
          bojee: bojeeLimitInfo,
          custom: customLimitInfo
        });
      } catch (error) {
        logger.error('Error checking message limits:', error);
      } finally {
        setIsCheckingMessageLimit(false);
      }
    };
    
    checkMessageLimits();
  }, [isAuthenticated, user, settings.learningLanguage, userPlan]);

  // Effect to sync fluent mode state from MongoDB
  useEffect(() => {
    const syncFluentModeState = async () => {
      if (!isAuthenticated || !user || !settings.learningLanguage) return;
      
      try {
        logger.debug('Syncing fluent mode usage state from MongoDB');
        const conversations = await getConversations(user.uid, settings.learningLanguage);
        
        if (conversations && conversations.length > 0) {
          // Get conversations for both modes
          const bojeeConversation = conversations.find(conv => conv.chatMode === 'bojee');
          const customConversation = conversations.find(conv => conv.chatMode === 'custom');
          
          // Update Bojee fluent mode state if needed
          if (bojeeConversation && bojeeConversation.usedFluentMode === true) {
            logger.debug('Setting hasUsedFluentModeInBojee = true from MongoDB');
            setHasUsedFluentModeInBojee(true);
          }
          
          // Update Custom fluent mode state if needed
          if (customConversation && customConversation.usedFluentMode === true) {
            logger.debug('Setting hasUsedFluentModeInCustom = true from MongoDB');
            setHasUsedFluentModeInCustom(true);
          }
        }
      } catch (error) {
        logger.error('Error syncing fluent mode state:', error);
      }
    };
    
    syncFluentModeState();
  }, [isAuthenticated, user, settings.learningLanguage, chatMode]);

  // Helper function to check if conversations should be reset
  const shouldResetConversations = () => {
    // Check if there's a reset flag in localStorage
    return localStorage.getItem('resetLanguageConversations') === 'true';
  };

  return (
    <div className="chatbot-container">
      <h1 className="page-title">{chatMode === 'custom' && customName ? `Chat with ${customName}` : 'Chat with Bojee'}</h1>

      {/* Add menu bar below header */}
      <div className="menu-bar">
        <button 
          className={`menu-button ${activeOverlay === 'chatMode' ? 'active' : ''}`}
          onClick={toggleChatMode}
        >
          Chat Mode
        </button>
        <button 
          className={`menu-button ${activeOverlay === 'politenessMode' ? 'active' : ''}`}
          onClick={togglePolitenessMode}
        >
          Politeness Mode
        </button>
      </div>

      <div className="chatbot-main">
        <div className="chatbot-chat">
          <div className="chatbot-messages" ref={chatContainerRef}>
            {messages.map((message, index) => (
              <div 
                key={index} 
                className={`chatbot-message ${message.role === 'user' ? 'user-message' : 'bot-message'}`}
              >
                <div className="chatbot-message-header">
                  {message.role === 'user' ? username : 
                   chatMode === 'custom' && customName ? customName : 'Bojee'}
                   
                  <div className="message-actions">
                    {message.role === 'user' && (
                      <button 
                        className="message-edit-btn"
                        onClick={() => handleEditStart(index)}
                        title="Edit message"
                      >
                        <EditIcon width="16" height="16" />
                      </button>
                    )}
                    <button 
                      className="message-delete-btn"
                      onClick={() => handleDeleteMessage(index)}
                      title="Delete message"
                    >
                      <DeleteIcon width="16" height="16" />
                    </button>
                  </div>
                </div>
                <div className="chatbot-message-content">
                  {editingMessageIndex === index ? (
                    <div className="edit-message-container">
                      <textarea
                        className="edit-message-textarea"
                        value={editedMessageContent}
                        onChange={(e) => setEditedMessageContent(e.target.value)}
                        rows={3}
                      />
                      <div className="edit-message-actions">
                        <button 
                          className="edit-message-save"
                          onClick={() => handleEditSave(index)}
                        >
                          Save
                        </button>
                        <button 
                          className="edit-message-cancel"
                          onClick={handleEditCancel}
                        >
                          Cancel
                        </button>
                      </div>
                    </div>
                  ) : (
                    getMessageContent(message, index)
                  )}
                  
                  {/* Add translate link for AI messages */}
                  {message.role === 'assistant' && !editingMessageIndex && (
                    <div className="translate-link-container">
                      <button 
                        className="translate-link"
                        onClick={() => toggleTranslation(index, message)}
                      >
                        {translatedMessages[index] ? 'Show Original' : 'Translate'}
                      </button>
                    </div>
                  )}
                </div>
              </div>
            ))}
            {isLoading && (
              <div className="chatbot-message bot-message">
                <div className="chatbot-message-header">
                  {chatMode === 'custom' && customName ? customName : 'Bojee'}
                </div>
                <div className="chatbot-message-content">
                  <div className="typing-indicator">
                    <span></span>
                    <span></span>
                    <span></span>
                  </div>
                </div>
              </div>
            )}
            <div ref={messagesEndRef} style={{ height: '30px', width: '100%', marginTop: '10px' }} />
          </div>
        </div>
      </div>
      
      {/* Input container fixed to bottom */}
      <div className="chatbot-input-container">
        <div className="chatbot-input-main">
          {/* Fluent mode toggle and My Score button row */}
          <div style={{ 
            display: 'flex', 
            justifyContent: 'space-between', 
            alignItems: 'center', 
            marginBottom: '10px' 
          }}>
            <div className="fluent-mode-container">
              <button
                className={`fluent-mode-toggle ${fluentMode ? 'on' : 'off'}`}
                onClick={toggleFluentMode}
                aria-label={fluentMode ? "Turn off fluent mode" : "Turn on fluent mode"}
                disabled={userPlan === 'pro'}
              >
                <span className="toggle-slider">
                  {fluentMode ? <BabbleFishOn className="babble-fish-icon on" /> : <BabbleFishOff className="babble-fish-icon off" />}
                  <span className="toggle-text">{fluentMode ? 'ON' : 'OFF'}</span>
                </span>
              </button>
              <span className="fluent-mode-label">Fluent Mode</span>
            </div>
            
            {/* My Score button - always visible but conditionally disabled with tooltip */}
            <button 
              className="my-score-button"
              onClick={handleRequestEvaluation}
              disabled={
                isEvaluating || 
                evaluationCount >= maxEvaluations || 
                messages.filter(m => m.role === 'user').length < 10 || 
                fluentMode || 
                (chatMode === 'bojee' && hasUsedFluentModeInBojee) || 
                (chatMode === 'custom' && hasUsedFluentModeInCustom) || 
                userPlan === 'pro'
              }
              title=""
              data-tooltip={
                (fluentMode || (chatMode === 'bojee' && hasUsedFluentModeInBojee) || (chatMode === 'custom' && hasUsedFluentModeInCustom))
                  ? "Evaluations are disabled when fluent mode is used. Please start a new convo to get evaluated." 
                  : messages.filter(m => m.role === 'user').length < 10 
                    ? "Evaluations are available after a minimum of 10 user messages" 
                    : evaluationCount >= maxEvaluations
                      ? "You've reached your daily evaluation limit"
                      : ""
              }
              style={{ 
                position: 'relative',
                margin: 0,
                padding: '8px 15px',
                maxWidth: '120px',
                opacity: (
                  isEvaluating || 
                  evaluationCount >= maxEvaluations || 
                  messages.filter(m => m.role === 'user').length < 10 || 
                  fluentMode || 
                  (chatMode === 'bojee' && hasUsedFluentModeInBojee) || 
                  (chatMode === 'custom' && hasUsedFluentModeInCustom) ||
                  userPlan === 'pro'
                ) ? 0.5 : 1,
                cursor: (
                  isEvaluating || 
                  evaluationCount >= maxEvaluations || 
                  messages.filter(m => m.role === 'user').length < 10 || 
                  fluentMode || 
                  (chatMode === 'bojee' && hasUsedFluentModeInBojee) || 
                  (chatMode === 'custom' && hasUsedFluentModeInCustom) ||
                  userPlan === 'pro'
                ) ? 'not-allowed' : 'pointer'
              }}
            >
              {isEvaluating ? 'Evaluating...' : 'My Score'}
            </button>
          </div>

          <div className="chatbot-textarea-wrapper" style={{ position: 'relative' }}>
            <textarea
              ref={inputRef}
              className={`chatbot-input ${isOverCharLimit ? 'over-limit' : ''}`}
              value={input}
              onChange={handleInputChange}
              onKeyDown={handleKeyDown}
              placeholder={
                userPlan === 'pro' 
                  ? "Bojee is only available for Premium users. Please upgrade your plan to access this feature."
                  : userPlan === 'canceled'
                    ? "Your subscription has been canceled. Please upgrade to continue chatting."
                    : isTrialExpired()
                      ? "Your trial has ended. Please upgrade to continue chatting."
                      : userPlan === 'trial' && messageLimit[chatMode]?.hasReached 
                        ? `As a trial user, you have reached your limit of ${messageLimit[chatMode]?.limit} messages for ${settings.learningLanguage} for today in ${chatMode === 'bojee' ? 'Bojee' : 'Custom'} mode. Please come back tomorrow.`
                        : "Type your message here..."
              }
              disabled={isLoading || (userPlan === 'trial' && messageLimit[chatMode]?.hasReached) || userPlan === 'pro' || isTrialExpired()}
            ></textarea>
            
            {/* Pro plan overlay with upgrade link */}
            {userPlan === 'pro' && (
              <div className="message-limit-overlay" style={{
                position: 'absolute',
                top: '0',
                left: '0',
                width: '100%',
                height: '100%',
                padding: '12px 15px',
                backgroundColor: '#f8f9fa',
                borderRadius: '6px',
                zIndex: '1',
                boxSizing: 'border-box',
                display: 'flex',
                alignItems: 'center',
                color: '#555',
                fontSize: '15px',
                pointerEvents: 'none',
                border: '1px solid #e9ecef'
              }}>
                <p style={{ margin: '0', pointerEvents: 'auto' }}>
                  Bojee is currently only available to Premium users. <a href="#" onClick={(e) => { e.preventDefault(); handleShowPricing(e); }} style={{ color: '#007bff', textDecoration: 'underline', fontWeight: 'bold', pointerEvents: 'auto' }}>Upgrade</a> to our Premium plan to get instant access!
                </p>
              </div>
            )}
            
            {/* Trial expired or subscription canceled overlay */}
            {isTrialExpired() && (
              <div className="message-limit-overlay" style={{
                position: 'absolute',
                top: '0',
                left: '0',
                width: '100%',
                height: '100%',
                padding: '12px 15px',
                backgroundColor: '#f8f9fa',
                borderRadius: '6px',
                zIndex: '1',
                boxSizing: 'border-box',
                display: 'flex',
                alignItems: 'center',
                color: '#555',
                fontSize: '15px',
                pointerEvents: 'none',
                border: '1px solid #e9ecef'
              }}>
                <p style={{ margin: '0', pointerEvents: 'auto' }}>
                  {getAccessLimitMessage()}
                </p>
              </div>
            )}
            
            {/* Message limit overlay with upgrade link */}
            {userPlan === 'trial' && !isTrialExpired() && messageLimit[chatMode]?.hasReached && (
              <div className="message-limit-overlay" style={{
                position: 'absolute',
                top: '0',
                left: '0',
                width: '100%',
                height: '100%',
                padding: '12px 15px',
                backgroundColor: '#f8f9fa',
                borderRadius: '6px',
                zIndex: '1',
                boxSizing: 'border-box',
                display: 'flex',
                alignItems: 'center',
                color: '#555',
                fontSize: '15px',
                pointerEvents: 'none',
                border: '1px solid #e9ecef'
              }}>
                <p style={{ margin: '0', pointerEvents: 'auto' }}>
                  {getAccessLimitMessage()}
                </p>
              </div>
            )}
            
            {isOverCharLimit && (
              <div className="character-limit-warning">
                {charOverLimit} characters over the limit
              </div>
            )}
          </div>
          
          <div className="chatbot-input-controls">
            <div className="right-buttons">
              <button 
                className="chatbot-new-conversation" 
                onClick={handleNewConversation}
                title="Start a new conversation"
                disabled={userPlan === 'pro'}
              >
                <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="currentColor">
                  <path d="M19.89 10.105a8.696 8.696 0 00-.789-1.456l-1.658 1.119a6.606 6.606 0 00-1.958-3.299A6.54 6.54 0 0012 5a6.538 6.538 0 00-3.485 1.469A6.606 6.606 0 006.558 9.77l-1.658-1.119a8.695 8.695 0 00-.789 1.456 8.683 8.683 0 00-.6 1.657l1.82.847a6.57 6.57 0 000 4.781l-1.82.847c.384 1.173.983 2.185 1.388 3.113A8.66 8.66 0 008.04 19l1.658-1.119a6.606 6.606 0 001.958 3.299A6.539 6.539 0 0012 19a6.538 6.538 0 003.485-1.469A6.606 6.606 0 0017.442 14.23l1.658 1.119a8.66 8.66 0 003.11-2.643c.406-.928 1.005-1.94 1.39-3.113l-1.82-.847a6.57 6.57 0 000-4.781l1.82-.847a8.683 8.683 0 00-.6-1.657M12 17a5 5 0 11-.001-10.001A5 5 0 0112 17" />
                </svg>
                New
              </button>
              
              <button 
                onClick={handleSendMessage}
                disabled={isLoading || isTransformingText || !input.trim() || isOverCharLimit || (userPlan === 'trial' && messageLimit[chatMode]?.hasReached) || userPlan === 'pro' || isTrialExpired()}
                className="send-button"
              >
                {isLoading || isTransformingText ? (
                  <span className="loader"></span>
                ) : (
                  "Send"
                )}
              </button>
            </div>
          </div>
        </div>
      </div>
      
      {/* Chat Mode Overlay */}
      {activeOverlay === 'chatMode' && (
        <div className="menu-overlay">
          <div className="overlay-header">
            <h3 className="overlay-title">Select Chat Mode</h3>
            <button className="close-overlay" onClick={closeOverlay}>✕</button>
          </div>
          <div className="menu-options">
            <div className="chatbot-mode-selector">
              <div className="chatbot-radio-group">
                <label className="chatbot-radio-label">
                  <input
                    type="radio"
                    name="chatMode"
                    value="bojee"
                    checked={chatMode === 'bojee'}
                    onChange={handleModeChange}
                  />
                  Bojee Mode
                </label>
                <p className="chatbot-mode-description">
                  Chat with Bojee, your friendly language assistant.
                </p>

                <label className="chatbot-radio-label">
                  <input
                    type="radio"
                    name="chatMode"
                    value="custom"
                    checked={chatMode === 'custom'}
                    onChange={handleModeChange}
                  />
                  Custom Mode
                </label>
                <p className="chatbot-mode-description">
                  Create your own custom AI language partner.
                </p>
              </div>
            </div>

            {chatMode === 'custom' && (
              <div className="custom-mode-container">
                <label className="home-menu-label">
                  Custom Name:
                  <input
                    type="text"
                    className="chatbot-custom-name"
                    value={customName}
                    onChange={handleCustomNameChange}
                    placeholder="Enter a name for your custom chatbot"
                    maxLength={30}
                  />
                </label>

                <label className="home-menu-label" style={{ marginTop: '10px' }}>
                  Custom Instructions:
                  <textarea
                    className="chatbot-custom-prompt"
                    value={customPrompt}
                    onChange={handleCustomPromptChange}
                    placeholder="Describe the role and personality of your language partner..."
                    rows={6}
                  />
                </label>
              </div>
            )}
          </div>
        </div>
      )}

      {/* Politeness Mode Overlay */}
      {activeOverlay === 'politenessMode' && (
        <div className="menu-overlay">
          <div className="overlay-header">
            <h3 className="overlay-title">Select Politeness Level</h3>
            <button className="close-overlay" onClick={closeOverlay}>✕</button>
          </div>
          <div className="menu-options">
            <div className="chatbot-radio-group">
              <label className="chatbot-radio-label">
                <input
                  type="radio"
                  name="politenessMode"
                  value="polite"
                  checked={politenessMode === 'polite'}
                  onChange={handlePolitenessModeChange}
                />
                Polite
              </label>
              <p className="chatbot-mode-description">
                Formal and respectful language, appropriate for professional settings and talking with elders or authority figures.
              </p>

              <label className="chatbot-radio-label">
                <input
                  type="radio"
                  name="politenessMode"
                  value="casual"
                  checked={politenessMode === 'casual'}
                  onChange={handlePolitenessModeChange}
                />
                Casual
              </label>
              <p className="chatbot-mode-description">
                Informal and relaxed language, appropriate for talking with friends, peers, and casual settings.
              </p>
            </div>
          </div>
        </div>
      )}

      {/* Dictionary Overlay */}
      {activeOverlay === 'dictionary' && dictionaryOpen && selectedWord && (
        <div className="dictionary-overlay">
          <div className="overlay-header">
            <h3 className="overlay-title">Dictionary</h3>
            <button className="close-overlay" onClick={() => {
              setDictionaryOpen(false);
              setActiveOverlay(null);
            }}>✕</button>
          </div>
          <ChatbotDictionaryPanel
            selectedWord={selectedWord}
            sentence={selectedSentence}
            language={settings.learningLanguage}
          />
        </div>
      )}
      
      {/* Tips Feature - positioned at bottom right */}
      <div className="tips-container" style={{ 
        position: 'fixed',
        bottom: '120px', /* Increased from 80px to avoid overlap with input container */
        right: '20px',
        display: 'flex', 
        alignItems: 'center',
        zIndex: 900
      }}>
        <img 
          src={tipsRead ? tipsOffIcon : tipsOnIcon} 
          alt="Tips" 
          style={{ 
            width: '32px',
            height: '32px',
            cursor: 'pointer',
            filter: tipsRead 
              ? 'grayscale(100%) brightness(5) invert(100%)' /* Make the off icon white */
              : 'brightness(5) invert(100%)' /* Make the on icon white */
          }}
          onClick={handleTipsClick}
        />
        
        {/* "Read me first" white container - only shows if tips haven't been read */}
        {!tipsRead && (
          <div 
            className="tips-popup-readme"
            onClick={handleTipsClick}
          >
            Read me first!
          </div>
        )}
      </div>

      {/* Tips Popup */}
      {showTips && (
        <div className="tips-popup-overlay" style={{
          animation: isClosing ? 'fadeOut 0.3s ease-in-out' : 'fadeIn 0.3s ease-in-out'
        }}>
          <div className="tips-popup" style={{
            animation: isClosing ? 'scaleOut 0.3s ease-in-out' : 'scaleIn 0.3s ease-in-out'
          }}>
            <h2 className="tips-popup-title">How to use:</h2>
            <hr className="tips-popup-divider" />
            <div className="tips-popup-content">
              <ul className="tips-popup-list">
                <li>The tools here were designed in tandem to address all your language learning needs.</li>
                <li>In <strong>Bojee Mode</strong>, you'll receive gentle corrections while keeping the conversation natural.</li>
                <li>In <strong>Custom Mode</strong>, you can personalize your chat experience with custom instructions and give your chat companion a custom name.</li>
                <li>Click on any word you don't understand to instantly have it display in the dictionary, which will provide context-aware definitions.</li>
                <li>You can save these words to highlight them in future chats and Daily Stories.</li>
                <li>When you write at least 10 messages, you can click the <strong>My Score</strong> button to get a thorough evaluation of your language skills and your current score. Note down your scores to track your progress!</li>
                <li>You can also turn on <strong>Fluent Mode</strong> when sending messages to transform them into native-level speech - a great way to see corrected versions of your messages for instant feedback. <i>(Note: "My Score" will be disabled for the current chat if "Fluent Mode" is used.)</i></li>
              </ul>
            </div>
            <div className="tips-popup-button-container">
              <button 
                onClick={handleTipsDismiss} 
                className="tips-popup-dismiss-button"
              >
                Dismiss
              </button>
            </div>
          </div>
        </div>
      )}
      
      {/* Pricing overlay */}
      {showPricingOverlay && <Pricing onClose={handleClosePricing} />}
    </div>
  );
};

export default Chatbot; 